import React, { useState } from 'react';
import {
  Button,
  createTheme,
  Grid,
  Paper,
  SelectChangeEvent,
  Stack,
  ThemeProvider,
} from '@mui/material';
import ButtonDropDown from '../button-drop-down/ButtonDropDown';
import FileUploadForm from '../file-upload-form/FileUploadForm';
import { useAppDispatch } from '../../common/hooks/useAppDispatch';
import { uploadDocument } from '../../slices/documents/documentsSlice';
import { DocumentVaultItemModel } from '../../models/document.vault.item.model';
import { NWClient } from '../../client/NWClient';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { ProgressOverlay } from '../progress-overlay/ProgressOverlay';
import { useAppSelector } from '../hooks/useAppSelector';
import { selectUser } from '../../slices/user/userSlice';
import { Employee } from '../../models/employee.model';
import { Supplier } from '../../models/supplier.model';
import { Customer } from '../../models/customer.model';
import { BusinessProfileContacts } from '../../slices/business-profile/businessProfileSlice';
import { useParams } from 'react-router-dom';
import { hideOverlay, showOverlay } from '../../slices/show-progress-overlay/showProgressOverlay';
import { TAGS_LABELS } from '../constants/tags-labels';
import { setTaskIds } from '../../slices/tasks/tasksSlice';
import { USER_PERMISSIONS } from '../constants/permissions';
import { useCheckPermission } from '../hooks/useCheckPermission';

interface FormUploadProps {
  categoryValue: 'business' | 'suppliers' | 'customers' | 'employment' | ''; //should be used for specific profile pages or tabs: Business, Customers, Suppliers, Employment
  categoriesAsOption?: boolean;
  onCancel: () => void;
  employees?: Employee[];
  suppliers?: Supplier[];
  customers?: Customer[];
  businessContacts?: BusinessProfileContacts[];
  workflow?: string | null;
  className?: string;
  accept?: string;
  supportedFormatsKey?: string;
}

const FormUpload: React.FC<FormUploadProps> = ({
  categoriesAsOption,
  onCancel,
  categoryValue,
  employees,
  suppliers,
  customers,
  businessContacts,
  workflow,
  className,
  accept,
  supportedFormatsKey,
}) => {
  const checkPermission = useCheckPermission();
  const { employeeId } = useParams();
  const { customerId } = useParams();
  const { supplierId } = useParams();
  const [assigned, setAssigned] = useState('');
  const [pendingStatus] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [selectedWorkflow, setSelectedWorkflow] = useState(workflow || '');
  const [category, setCategory] = useState<string>(categoryValue || '');
  const dispatch = useAppDispatch();
  const localTheme = createTheme({});
  const currentUser = useAppSelector(selectUser);

  const { t } = useTranslation();

  const documentWorkflowLabelsEmployment = TAGS_LABELS.documentWorkflow.employment.map((el) =>
    t(el)
  );
  const documentWorkflowLabelsEmploymentSorted = documentWorkflowLabelsEmployment.sort((a, b) =>
    a.localeCompare(b)
  );
  const documentWorkflowLabelsSupplier = TAGS_LABELS.documentWorkflow.supplier.map((el) => t(el));
  const documentWorkflowLabelsSupplierSorted = documentWorkflowLabelsSupplier.sort((a, b) =>
    a.localeCompare(b)
  );
  const documentWorkflowLabelsCustomer = TAGS_LABELS.documentWorkflow.customer.map((el) => t(el));
  const documentWorkflowLabelsCustomerSorted = documentWorkflowLabelsCustomer.sort((a, b) =>
    a.localeCompare(b)
  );
  const documentWorkflowLabelsBusiness = TAGS_LABELS.documentWorkflow.business.map((el) => t(el));
  const documentWorkflowLabelsBusinessSorted = documentWorkflowLabelsBusiness.sort((a, b) =>
    a.localeCompare(b)
  );

  const employeesOptions = employees?.map((employee) => ({
    value: employee.id.toString(),
    label: `${employee.full_name}`,
  }));

  const suppliersOptions = suppliers?.map((supplier) => ({
    value: supplier.id.toString(),
    label: `${supplier.name}`,
  }));

  const customersOptions = customers?.map((customer) => ({
    value: customer.id.toString(),
    label: `${customer.customer_name}`,
  }));

  const businessContactsOptions = businessContacts?.map((contact) => ({
    value: contact.id.toString(),
    label: `${contact.contact_name}`,
  }));

  const categoryOptions: {
    value: 'business' | 'suppliers' | 'customers' | 'employment';
    label: string;
  }[] = [];

  if (checkPermission(USER_PERMISSIONS.ORG_DOCUMENT_UPLOAD_BUSINESS_MANAGE)) {
    categoryOptions.push({ value: 'business', label: t('tagsLabels.categories.business') });
  }
  if (checkPermission(USER_PERMISSIONS.ORG_DOCUMENT_UPLOAD_CUSTOMER_MANAGE)) {
    categoryOptions.push({ value: 'customers', label: t('tagsLabels.categories.customers') });
  }
  if (checkPermission(USER_PERMISSIONS.ORG_DOCUMENT_UPLOAD_EMPLOYEE_MANAGE)) {
    categoryOptions.push({ value: 'employment', label: t('tagsLabels.categories.employment') });
  }
  if (checkPermission(USER_PERMISSIONS.ORG_DOCUMENT_UPLOAD_SUPPLIER_MANAGE)) {
    categoryOptions.push({ value: 'suppliers', label: t('tagsLabels.categories.suppliers') });
  }

  const getProfileNames = () => {
    switch (category) {
      case 'employment':
        return employeesOptions;
      case 'customers':
        return customersOptions;
      case 'suppliers':
        return suppliersOptions;
      case 'business':
        return businessContactsOptions;
      default:
        return [];
    }
  };

  const workflowsArray = () => {
    switch (category) {
      case 'employment':
        return documentWorkflowLabelsEmploymentSorted.map((el) => ({ label: el, value: el }));
      case 'suppliers':
        return documentWorkflowLabelsSupplierSorted.map((el) => ({ label: el, value: el }));
      case 'customers':
        return documentWorkflowLabelsCustomerSorted.map((el) => ({ label: el, value: el }));
      case 'business':
        return documentWorkflowLabelsBusinessSorted.map((el) => ({ label: el, value: el }));
      default:
        return [];
    }
  };

  const handleCategoryChange = (event: SelectChangeEvent) => {
    setCategory(event.target.value);
    setAssigned('');
  };

  const handleAssignedChange = (event: SelectChangeEvent) => {
    setAssigned(event.target.value);
  };

  const handleWorkflowChange = (event: SelectChangeEvent) => {
    setSelectedWorkflow(event.target.value);
  };

  const getProfileLabelsProp = () => {
    if (category === 'employment') {
      return 'employees';
    } else if (category === 'business') {
      return 'business_profile_contacts';
    } else {
      return category;
    }
  };

  /*useEffect(() => {
          if (employees?.length && category === 'employment') {
            setAssigned(employees[0].id.toString());
          }
        }, [employees, category]);
      
        useEffect(() => {
          if (customers?.length && category === 'customers') {
            setAssigned(customers[0].id.toString());
          }
        }, [customers, category]);
      
        useEffect(() => {
          if (suppliers?.length && category === 'suppliers') {
            setAssigned(suppliers[0].id.toString());
          }
        }, [suppliers, category]);*/

  const handleSave = () => {
    if (!selectedFiles.length) return;
    dispatch(showOverlay());
    const data = new FormData();
    selectedFiles.forEach((file) => {
      data.append('files', file);
    });
    const workflowToUse = workflow || selectedWorkflow;
    data.append('category', categoriesAsOption ? category.toLowerCase() : categoryValue);
    workflowToUse &&
      data.append(
        'du_workflows',

        workflowToUse
      );
    data.append('user', String(currentUser.id));

    const profileToAppend =
      (customerId && Number(customerId)) ||
      (employeeId && Number(employeeId)) ||
      (supplierId && Number(supplierId)) ||
      Number(assigned);

    if (assigned || customerId || employeeId || supplierId) {
      data.append(getProfileLabelsProp(), String(profileToAppend));
    }

    dispatch(uploadDocument({ data }))
      .unwrap()
      .then((res: DocumentVaultItemModel) => {
        dispatch(hideOverlay());
        if (res.task_ids) {
          dispatch(setTaskIds(res.task_ids));
        }
        setSelectedFiles([]);
        NWClient.patch('tag-document-upload', res.id, {
          du_workflows:
            workflowToUse !== t('headers.general') && workflowToUse !== '' ? [workflowToUse] : [],
        })
          .then(() => {
            NWClient.post('document-extras-extraction', { document_upload_id: res.id });
          })
          .then(() => {
            // toast.success(t('messages.documentUploaded'));
            //createDocumentHTML(res.id);
          })
          .finally(() => {
            onCancel();
          });
      })
      .catch((error) => {
        onCancel();
        dispatch(hideOverlay());
        toast.error(error.message || t('messages.errorOccurred'));
      })
      .finally(() => {
        onCancel();
      });
  };

  return (
    <div>
      {pendingStatus ? (
        <ProgressOverlay />
      ) : (
        <ThemeProvider theme={localTheme}>
          <Paper
            onClick={onCancel}
            sx={{
              backgroundColor: 'transparent',
              position: 'fixed',
              left: 0,
              right: 0,
              top: 0,
              bottom: 0,
              zIndex: 1000,
              boxShadow: 'none',
            }}
          />
          <Paper
            sx={{ borderColor: '#6414db', position: 'relative', zIndex: 1005 }}
            elevation={0}
            className={className ? 'form-upload__paper' + ' ' + className : 'form-upload__paper'}
          >
            <Stack spacing={3}>
              <FileUploadForm
                onFileSelect={(files) => setSelectedFiles(files)}
                accept={accept}
                supportedFormatsKey={supportedFormatsKey}
              />
              <Stack direction='row' gap={'16px'} flexWrap={'wrap'}>
                {categoriesAsOption && (
                  <Grid flexGrow={1} width={'100%'} maxWidth={'calc(50% - 8px)'}>
                    <ButtonDropDown
                      label={t('labels.chooseProfile')}
                      value={category}
                      onChange={handleCategoryChange}
                      options={categoryOptions}
                      defaultValue={categoryValue}
                      required
                      placeholder={t('labels.profile')}
                    />
                  </Grid>
                )}
                {!(customerId || employeeId || supplierId) && (
                  <Grid flexGrow={1} width={'100%'} maxWidth={'calc(50% - 8px)'}>
                    <ButtonDropDown
                      label={t('labels.assignTo')}
                      value={assigned}
                      onChange={handleAssignedChange}
                      options={[
                        ...[{ value: '', label: t('labels.noAssignee') }],
                        ...getProfileNames(),
                      ]}
                      placeholder={t('labels.chooseAssignee')}
                      disabled={
                        !category ||
                        getProfileNames().length === 0 ||
                        !checkPermission(USER_PERMISSIONS.ORG_DOCUMENT_UPLOAD_ASSIGN_PROFILE)
                      }
                    />
                  </Grid>
                )}
                {!workflow && (
                  <Grid flexGrow={1} width={'100%'} maxWidth={'calc(50% - 8px)'}>
                    <ButtonDropDown
                      label={t('labels.workflow')}
                      value={selectedWorkflow}
                      onChange={handleWorkflowChange}
                      options={[
                        ...[{ value: '', label: t('labels.generalUnassigned') }],
                        ...workflowsArray(),
                      ]}
                      placeholder={t('labels.chooseWorkflows')}
                      disabled={!category || workflowsArray().length === 0}
                    />
                  </Grid>
                )}
              </Stack>
              <Stack
                direction='row'
                gap={'8px'}
                justifyContent='flex-end'
                className='form-upload__button-group'
              >
                <Button
                  variant='contained'
                  className='btn-default btn-action-basic btn-cancel'
                  onClick={onCancel}
                >
                  {t('buttons.cancel')}
                </Button>
                <Button
                  variant='contained'
                  className='btn-default btn-action-basic'
                  onClick={handleSave}
                  disabled={
                    categoriesAsOption ? !category || !selectedFiles.length : !selectedFiles.length
                  }
                >
                  {t('buttons.save')}
                </Button>
              </Stack>
            </Stack>
          </Paper>
        </ThemeProvider>
      )}
    </div>
  );
};

export default FormUpload;
